/**
 * Share pay on fixed jobs (docs/plans/people.md P7). Pure — no I/O. The books run the same maths
 * (battlebooks/functions/src/lib/sharePay.ts — keep the two in step) and publish each person's share to their own
 * opsJobPay doc; the phone reads that doc and only adds the running timer on top.
 *
 * Lj's decisions (Oct 11):
 *   - pay follows the JOB TYPE, with per-person overrides: shares count on fixed (lump-sum) and product jobs; an
 *     hourly (T&M) job has no shares — each person earns their hours × THEIR pay rate and the job bills the hours at
 *     the role's bill rate (jobHourly below); internal / personal jobs have no earned line (jobpay.js, v0.1.258);
 *   - a task's labour is split per task, by the hours each person put on it, weighted by their role share %
 *     (the profile's own %, else their role's, else 100 — an apprentice < a journeyman);
 *   - the labour a task is worth = the bid line's labour $ when the task came from a bid, else
 *     hours × each person's bill rate by role (billRoles, never their pay rate);
 *   - a non-admin only ever sees what a task is worth to THEM — never the task's or the job's number;
 *   - a SCORE unless the person's pay type says task share and the books setting "pay shares through payroll" is on.
 *
 * Weights are relative: the people on a task share 100% of its labour between them (a journeyman alone on a task
 * earns all of it; with an apprentice at 60% beside him for the same hours, 100 : 60).
 */

import { isVoidTask } from './projectclose.js';
import { SHARE_JOB_TYPES } from './jobpay.js';   // fixed + product (pay by job type, v0.1.258)

const H = 3600000;
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const pos = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 0);


/**
 * Who put in a stretch of task time: the entry's own uid, else the clock session (ownerUid) it falls inside —
 * the one on this task's project first — else the task's assignee, else its owner.
 */
export function entryUid(entry, task, sessions = []) {
  if (entry?.uid) return entry.uid;
  const end = Number(entry?.createdAt) || 0;
  const start = end - pos(entry?.duration);
  if (end > 0) {
    const hits = sessions.filter((s) => s && s.ownerUid && !s.overlay && s.type !== 'travel' && !s.deletedAt
      && Number(s.startedAt) <= end && (s.endedAt == null || Number(s.endedAt) >= start));
    const onJob = hits.filter((s) => task?.projectId && s.projectId === task.projectId);
    const pick = (list) => {
      const uids = [...new Set(list.map((s) => s.ownerUid))];
      if (uids.length === 1) return uids[0];
      if (uids.length > 1 && task?.assignee && uids.includes(task.assignee)) return task.assignee;
      return null;
    };
    const who = pick(onJob) || pick(hits);
    if (who) return who;
  }
  return task?.assignee || task?.ownerUid || null;
}

/**
 * Hours on one task by person: Map(uid → hours). `running` = { uid, ms } — the timer running on it now (live).
 * Old tasks with time but no entries count their timeSpent for the assignee.
 */
export function taskHoursByUid(task, { sessions = [], running = null } = {}) {
  const out = new Map();
  const add = (uid, ms) => { if (uid && ms > 0) out.set(uid, (out.get(uid) || 0) + ms / H); };
  const entries = Array.isArray(task?.timeEntries) ? task.timeEntries : [];
  if (entries.length) for (const e of entries) add(entryUid(e, task, sessions), pos(e?.duration));
  else add(task?.assignee || task?.ownerUid || null, pos(task?.timeSpent));
  if (running?.uid && running.ms > 0) add(running.uid, running.ms);
  return out;
}

/**
 * What a task's labour is worth: the bid line's labour $ when it came from a bid (`bidLabour`), else
 * Σ hours × the bill rate of each person's role. → { value, basis: 'bid' | 'rate' | null, missingRates: [uid] }
 */
export function taskLabourValue({ bidLabour = null, hours = new Map(), billRateOf = () => null } = {}) {
  if (pos(bidLabour) > 0) return { value: r2(bidLabour), basis: 'bid', missingRates: [] };
  let value = 0; const missingRates = [];
  for (const [uid, h] of hours) {
    const rate = pos(billRateOf(uid));
    if (rate > 0) value += h * rate; else if (h > 0) missingRates.push(uid);
  }
  return { value: value > 0 ? value : 0, basis: value > 0 ? 'rate' : null, missingRates };
}

/** Unrounded split: [{ uid, hours, weight, pct, raw }] — pct = hours × weight ÷ Σ. Nobody with no weighted hours. */
function rawSplit(value, hours, weightOf) {
  const rows = [];
  for (const [uid, h] of hours) {
    const weight = Math.max(0, Number(weightOf(uid)));
    const points = h * (Number.isFinite(weight) ? weight : 100);
    if (points > 0) rows.push({ uid, hours: h, weight: Number.isFinite(weight) ? weight : 100, points });
  }
  const total = rows.reduce((t, r) => t + r.points, 0);
  return rows.map((r) => ({ uid: r.uid, hours: r.hours, weight: r.weight, pct: total > 0 ? r.points / total : 0, raw: total > 0 ? (value * r.points) / total : 0 }));
}

/**
 * Split a task's labour between the people who worked it, rounded to the cent so the shares add up to the value
 * exactly (largest remainder). → [{ uid, hours, weight, pct (0–100, 1 dp), amount }], largest first.
 */
export function splitShares(value, hours = new Map(), weightOf = () => 100) {
  const total = r2(value);
  const rows = rawSplit(total, hours, weightOf);
  if (!rows.length || !(total > 0)) return [];
  const cents = Math.round(total * 100);
  const floors = rows.map((r) => Math.floor(r.raw * 100 + 1e-9));
  let left = cents - floors.reduce((t, c) => t + c, 0);
  const order = rows.map((r, i) => ({ i, rem: r.raw * 100 - floors[i] })).sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const o of order) { if (left <= 0) break; floors[o.i] += 1; left -= 1; }
  return rows.map((r, i) => ({ uid: r.uid, hours: Math.round(r.hours * 100) / 100, weight: r.weight, pct: Math.round(r.pct * 1000) / 10, amount: floors[i] / 100 }))
    .sort((a, b) => b.amount - a.amount || String(a.uid).localeCompare(String(b.uid)));
}

/** One person's unrounded share of a task at `hours` (their own hours swapped in). */
function shareAt(uid, myHours, { bidLabour, hours, billRateOf, weightOf }) {
  const h = new Map(hours); h.set(uid, myHours);
  const { value } = taskLabourValue({ bidLabour, hours: h, billRateOf });
  return rawSplit(value, h, weightOf).find((r) => r.uid === uid)?.raw || 0;
}

/**
 * What another hour of this person's time on the task would add to their share, right now ($/h) — what the
 * phone adds per running hour between two publishes. Bid-priced and alone on it: 0 (the line is theirs already).
 */
export function marginalPerHour(uid, { bidLabour = null, hours = new Map(), billRateOf = () => null, weightOf = () => 100 } = {}) {
  const h0 = hours.get(uid) || 0;
  const step = 0.25;
  const a = shareAt(uid, h0, { bidLabour, hours, billRateOf, weightOf });
  const b = shareAt(uid, h0 + step, { bidLabour, hours, billRateOf, weightOf });
  return Math.max(0, r2((b - a) / step));
}

/** Everything about one task's shares. → { value, basis, missingRates, shares: [...] (each + perHour) } */
export function taskShares({ bidLabour = null, hours = new Map(), billRateOf = () => null, weightOf = () => 100 } = {}) {
  const { value, basis, missingRates } = taskLabourValue({ bidLabour, hours, billRateOf });
  const shares = splitShares(value, hours, weightOf).map((s) => ({ ...s, perHour: marginalPerHour(s.uid, { bidLabour, hours, billRateOf, weightOf }) }));
  return { value: r2(value), basis, missingRates, shares };
}

/** Do shares count on this project? The job type (jobTypeOf) is fixed — or a person's job pay says 'share'. */
export const shareJob = (jobType) => SHARE_JOB_TYPES.has(jobType);

/**
 * Is this person PAID by share on this job (payroll takes it, when the books setting is on)? Their pay on the job
 * (opsJobPay.payType) wins; else their profile pay type. Everyone else's share is a score.
 */
export function paidByShare(profilePayType, jobPay = null) {
  if (jobPay?.payType) return jobPay.payType === 'share';
  return profilePayType === 'share';
}

/**
 * Shares on one job, for everyone who worked it. Bid labour per task comes from `bidLabourOf(task)` (the books
 * know the bid lines); a parent whose subtasks came from their own bid lines keeps only what's left over.
 * Tasks paid as a piece (task.piece) are left out — the piece is their pay.
 * → { byUid: { [uid]: { total, counted, done, open, hours, tasks: { [taskId]: { amount, hours, pct, perHour, basis, done, doneAt } } } },
 *     tasks: [{ taskId, value, basis, missingRates, shares }] }
 */
export function jobShares({ tasks = [], sessions = [], bidLabourOf = () => null, billRateOf = () => null, weightOf = () => 100 } = {}) {
  const kids = new Map();
  for (const t of tasks) if (t?.parentId) { const a = kids.get(t.parentId) || []; a.push(t); kids.set(t.parentId, a); }
  const byUid = {};
  const rows = [];
  for (const t of tasks) {
    if (!t || t.deletedAt || t.bucket || isVoidTask(t) || (t.piece?.uid && pos(t.piece.price) > 0)) continue;   // void = closed with its project (v0.1.260): never finished, pays nothing (books twin: v0.7.119)
    const hours = taskHoursByUid(t, { sessions });
    if (!hours.size) continue;
    let bid = pos(bidLabourOf(t));
    if (bid > 0) for (const k of kids.get(t.id) || []) bid -= pos(bidLabourOf(k));
    const r = taskShares({ bidLabour: bid > 0 ? bid : null, hours, billRateOf, weightOf });
    rows.push({ taskId: t.id, value: r.value, basis: r.basis, missingRates: r.missingRates, shares: r.shares });
    const done = t.status === 'done';
    for (const s of r.shares) {
      const p = byUid[s.uid] || (byUid[s.uid] = { total: 0, counted: 0, done: 0, open: 0, hours: 0, tasks: {} });
      p.tasks[t.id] = { amount: s.amount, hours: s.hours, pct: s.pct, perHour: done ? 0 : s.perHour, basis: r.basis, done, doneAt: done ? t.completedAt || null : null };
      p.total = r2(p.total + s.amount);
      p.hours = Math.round((p.hours + s.hours) * 100) / 100;
      if (done) { p.counted = r2(p.counted + s.amount); p.done += 1; } else p.open += 1;
    }
  }
  return { byUid, tasks: rows };
}

/**
 * The earned line, live: the published share of this task plus the running timer at the published marginal rate.
 * `pub` = opsJobPay.share.tasks[taskId] (or null), `runningMs` = this person's timer on the task now.
 * → { amount, live: bool, done } | null when the task earns them nothing (yet).
 */
export function liveEarned(pub, runningMs = 0) {
  if (pub?.mode === 'hourly') return liveHourly(pub, runningMs);
  const extra = !pub?.done && pos(runningMs) > 0 ? pos(pub?.perHour) * (runningMs / H) : 0;
  const amount = r2(pos(pub?.amount) + extra);
  if (!(amount > 0)) return null;
  return { amount, live: extra > 0, done: !!pub?.done };
}

/** An hourly (T&M) task, live: your hours, what they bill, your pay (published; the timer adds at the same rates). */
function liveHourly(pub, runningMs = 0) {
  const runH = !pub?.done && pos(runningMs) > 0 ? runningMs / H : 0;
  const hours = Math.round((pos(pub?.hours) + runH) * 100) / 100;
  if (!(hours > 0)) return null;
  const billed = pub?.billed == null ? null : r2(pos(pub.billed) + runH * pos(pub.rate));
  const pay = pub?.pay == null ? null : r2(pos(pub.pay) + runH * pos(pub.payRate));
  return { mode: 'hourly', hours, billed, pay, live: runH > 0, done: !!pub?.done };
}

const dollars = (v) => `$${v.toLocaleString('en-CA', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
const hoursText = (h) => `${Math.round(h * 10) / 10} h`;

/** "$140 earned on this task" / "$62.50 earned so far" — on an hourly job "6.5 h · $552 billed". */
export function earnedText(e) {
  if (!e) return null;
  if (e.mode === 'hourly') return e.billed != null && e.billed > 0 ? `${hoursText(e.hours)} · ${dollars(e.billed)} billed` : `${hoursText(e.hours)} · no bill rate yet`;
  const v = dollars(e.amount);
  return e.done ? `${v} earned on this task` : `${v} earned so far`;
}

/** An hourly line's pay part ("your pay $182") — only from your own job-pay doc (you and admins, SYNC §3.9). */
export const earnedPayText = (e) => (e?.mode === 'hourly' && e.pay != null && e.pay > 0 ? `your pay ${dollars(e.pay)}` : null);

const isTravelTask = (t) => t?.quickTaskId === 'travel' || (t?.tags || []).some((x) => String(x).replace(/^#/, '').toLowerCase() === 'travel');

/**
 * An hourly (T&M) job: no shares. Each person's hours on each task, what they bill (hours × their bill rate — the
 * project's role rate, else its rate, else the org's role rate; travel tasks at the project's travelRate when set) and
 * what they're paid (hours × THEIR pay rate, what payroll pays; null when it isn't known). Piece tasks left out.
 * → { byUid: { [uid]: { mode: 'hourly', hours, billed, pay, done, open, tasks: { [taskId]: { mode, hours, billed, rate, pay, payRate, done, doneAt } } } } }
 */
export function jobHourly({ tasks = [], sessions = [], billRateOf = () => null, payRateOf = () => null, travelRate = null } = {}) {
  const byUid = {};
  for (const t of tasks) {
    if (!t || t.deletedAt || (t.piece?.uid && pos(t.piece.price) > 0)) continue;
    const hours = taskHoursByUid(t, { sessions });
    const done = t.status === 'done';
    const travel = isTravelTask(t) && pos(travelRate) > 0;
    for (const [uid, h] of hours) {
      if (!(h > 0)) continue;
      const rate = travel ? pos(travelRate) : pos(billRateOf(uid));
      const payRate = pos(payRateOf(uid));
      const p = byUid[uid] || (byUid[uid] = { mode: 'hourly', hours: 0, billed: 0, pay: null, done: 0, open: 0, tasks: {} });
      const row = { mode: 'hourly', hours: Math.round(h * 100) / 100, billed: rate ? r2(h * rate) : null, rate: rate || null, pay: payRate ? r2(h * payRate) : null, payRate: payRate || null, done, doneAt: done ? t.completedAt || null : null };
      p.tasks[t.id] = row;
      p.hours = Math.round((p.hours + row.hours) * 100) / 100;
      if (row.billed != null) p.billed = r2(p.billed + row.billed);
      if (row.pay != null) p.pay = r2((p.pay || 0) + row.pay);
      if (done) p.done += 1; else p.open += 1;
    }
  }
  return { byUid };
}

/**
 * The Pay tab's week: this person's shares on tasks finished in [from, to) across their job pay docs, and what's
 * building on open tasks. `jobPays` = their opsJobPay docs. → { amount, tasks, open, openTasks }
 */
export function sharesInRange(jobPays = [], { from = 0, to = Infinity } = {}) {
  let amount = 0; let tasks = 0; let open = 0; let openTasks = 0;
  for (const jp of jobPays) {
    if (jp?.share?.mode === 'hourly') continue;   // an hourly job's hours are wages, not shares
    for (const t of Object.values(jp?.share?.tasks || {})) {
      if (t.done) { const at = Number(t.doneAt) || 0; if (at >= from && at < to) { amount += pos(t.amount); tasks += 1; } }
      else if (pos(t.amount) > 0) { open += pos(t.amount); openTasks += 1; }
    }
  }
  return { amount: r2(amount), tasks, open: r2(open), openTasks };
}

/** Your share doc for a task's project: opsJobPay/{projectId}__{uid}. */
export const myShareOf = (jobPays = [], projectId, uid, taskId) => {
  const jp = jobPays.find((j) => j && j.projectId === projectId && j.uid === uid) || null;
  return jp?.share?.tasks?.[taskId] || null;
};
